import { useEffect, useRef, useState } from 'react';
import { Game, HudState, FloatingTextEvent, MatchResultInternal } from '../game/Game';
import { getWeapon } from '../game/weapons';
import { startSession, submitScore } from '../api/client';
import { MatchResult, Player } from '../types';

interface Props {
  player: Player;
  weaponId: string;
  onFinished: (result: MatchResult) => void;
}

interface FloatItem extends FloatingTextEvent {
  id: number;
}

let idCounter = 0;

export default function GameScreen({ player, weaponId, onFinished }: Props) {
  const mountRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Game | null>(null);
  const sessionRef = useRef<string | null>(null);
  const [hud, setHud] = useState<HudState | null>(null);
  const [floats, setFloats] = useState<FloatItem[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const session = await startSession(player.token, weaponId, 'desert_range', 'classic');
        if (cancelled) return;
        sessionRef.current = session.sessionId;

        const weapon = getWeapon(weaponId);
        if (!mountRef.current) return;

        gameRef.current = new Game(mountRef.current, weapon, {
          onHud: (h) => setHud(h),
          onFloatingText: (e) => {
            const id = idCounter++;
            setFloats((prev) => [...prev, { ...e, id }]);
            setTimeout(() => {
              setFloats((prev) => prev.filter((f) => f.id !== id));
            }, 900);
          },
          onGameOver: async (result: MatchResultInternal) => {
            const accuracy = result.shots > 0 ? result.hits / result.shots : 0;
            const matchResult: MatchResult = {
              score: result.score,
              shots: result.shots,
              hits: result.hits,
              durationSeconds: result.durationSeconds,
              accuracy,
            };
            try {
              if (sessionRef.current) {
                const submitRes = await submitScore(player.token, sessionRef.current, matchResult);
                matchResult.coinsEarned = submitRes.coinsEarned;
              }
            } catch (e) {
              // Score submission failed (network or anti-cheat rejection); the
              // player still sees their local result, it just won't be on
              // the global leaderboard.
              console.error('Score submission failed:', e);
            }
            onFinished(matchResult);
          },
        });
        setStatus('ready');
      } catch (e: any) {
        setErrorMsg(e.message || 'Could not start match.');
        setStatus('error');
      }
    }

    init();

    return () => {
      cancelled = true;
      gameRef.current?.dispose();
      gameRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timeSeconds = hud ? Math.max(0, hud.timeLeftMs / 1000) : 0;
  const isCritical = timeSeconds < 8;

  return (
    <div className="game-viewport">
      <div className="game-canvas-mount" ref={mountRef} />

      {status === 'loading' && (
        <div className="screen">
          <div className="loading-text">Loading Desert Range...</div>
        </div>
      )}

      {status === 'error' && (
        <div className="screen">
          <div className="panel">
            <div className="error-text">{errorMsg}</div>
          </div>
        </div>
      )}

      {status === 'ready' && hud && (
        <>
          <div className="crt-overlay" />
          <div className="vignette-overlay" />
          <div className="crosshair" style={{ left: '50%', top: '50%' }} />

          <div className="hud-top">
            <div className={`hud-chip ${isCritical ? 'timer-critical' : ''}`}>
              <span className="label">TIME</span>
              <span className="value">{timeSeconds.toFixed(1)}s</span>
            </div>
            <div className="hud-chip">
              <span className="label">SCORE</span>
              <span className="value">{hud.score.toLocaleString()}</span>
            </div>
          </div>

          <div className="hud-bottom">
            <div className={`ammo-chip ${hud.reloading ? 'reloading' : ''}`}>
              {hud.weaponName.toUpperCase()} · {hud.reloading ? 'RELOADING' : `${hud.ammo}/${hud.magazineSize}`}
            </div>
            <div className="combo-chip">{hud.combo > 1 ? `${hud.combo}x COMBO` : ''}</div>
          </div>

          {floats.map((f) => (
            <div
              key={f.id}
              className={`floating-text ${f.kind}`}
              style={{ left: f.screenX, top: f.screenY }}
            >
              {f.text}
            </div>
          ))}
          {floats
            .filter((f) => f.kind === 'hit' || f.kind === 'bonus')
            .map((f) => (
              <div key={`marker-${f.id}`} className="hit-marker" style={{ left: f.screenX, top: f.screenY }} />
            ))}
        </>
      )}
    </div>
  );
}
